import React, { useCallback, useRef, useState } from 'react';
import { FlexRow, TableDataState, TableEditButtons, usePaginatedQueryV8 } from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './taxoffice-filters';
import { DataGrid } from './taxoffice-grid';
import { handleExport } from './taxoffice-export-columns';
import { useApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';
import { isFotokiklosi } from 'shared-pages/common/constants';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const TaxofficeGridPage = observer(
  ({ collectorId, isGridInForm = false }: { collectorId?: number; isGridInForm?: boolean }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('TaxofficeGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const { t } = useTranslation();

    const { canView, canAddNew, canEdit, canDelete } = userPermissions();
    function userPermissions() {
      if (isFotokiklosi) {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      } else {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollector)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      }
    }

    const persistedValues = isGridInForm ? { collectorId } : {};

    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'name ASC';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      { isActive: true, collectorId: collectorId },
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'taxoffices_grid',
      persistedValues,
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const gridRef = useRef();

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header>
              <FlexRow>
                <Card.Title className="m-0">{t('Δ.Ο.Υ.')}</Card.Title>
                <TableEditButtons
                  className="me-3"
                  canEdit={canEdit}
                  canInsert={canAddNew}
                  canDelete={canDelete}
                  tableOptionsMeta={() => {
                    if (gridRef.current) return (gridRef.current as any).table.options.meta;
                    else return undefined;
                  }}
                  twoLines={false}
                />
              </FlexRow>
            </Card.Header>
          )}
          <Card.Body className="nopadding">
            {/* Filters */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? {}}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                if (!isGridInForm) {
                  history.replace({
                    pathname: location.pathname,
                    state: {
                      ...location.state,
                      order: orderBy,
                    },
                  });
                }
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);

export default TaxofficeGridPage;
